import { GlobalState } from "domain/types";
import { REQUEST_STATUS } from "lib/types";
import { createSelector } from "reselect";

export const getCoreState = (state: GlobalState) => state.core;

export const getValidateFetchStatus = createSelector(
  getCoreState,
  (state) => state.validateAddressRequestStatus,
);
export const getBillEstimateFetchStatus = createSelector(
  getCoreState,
  (state) => state.billingEstimateRequestStatus,
);

export const getBillEstimate = createSelector(getCoreState, (state) =>
  state.billEstimates?.find(
    (estimate) => estimate.product_name === state.selectedProduct,
  ),
);

export const getBillEstimates = createSelector(getCoreState, (state) =>
  state.billEstimates ? state.billEstimates : undefined,
);

export const getMonthlyElectricityUsage = createSelector(
  getBillEstimate,
  (billEstimate) => {
    if (!billEstimate) {
      return 0;
    }
    return billEstimate.days * billEstimate.consumption_per_day;
  },
);

export const getChargePerKWH = createSelector(
  getBillEstimate,
  getMonthlyElectricityUsage,
  (billEstimate, monthlyElecUsage) => {
    if (!billEstimate) return 0;
    return parseFloat(billEstimate.current_charges_estimate) / monthlyElecUsage;
  },
);

export const getSelectedAddress = createSelector(
  getCoreState,
  (state) => state.selectedAddress,
);
export const getFlickAccountError = createSelector(
  getCoreState,
  (state) => state.flickAccountError,
);

export const getSelectedEligibilityType = createSelector(
  getCoreState,
  (state) => state.selectedEligibilityType,
);

export const getEligibleAddresses = createSelector(
  getCoreState,
  (state) => state.eligibleAddresses,
);

export const getUserId = createSelector(getCoreState, (state) => state.userId);

export const getVerificationId = createSelector(
  getCoreState,
  (state) => state.verifyId,
);

export const getVerificationFetchStatus = createSelector(
  getCoreState,
  (state) => state.verificationRequestStatus,
);

export const getVerifyCodeFetchStatus = createSelector(
  getCoreState,
  (state) => state.verifyCodeRequestStatus,
);

export const getCompleteRegistrationRequestStatus = createSelector(
  getCoreState,
  (state) => state.completeRegistrationRequestStatus,
);

export const getUpdateFlickSignUpRequestStatus = createSelector(
  getCoreState,
  (state) => state.updateFlickSignUpRequestStatus,
);

export const getExistingZAccount = createSelector(
  getCoreState,
  (state) => state.userId !== null,
);

export const getFlickUserId = createSelector(
  getCoreState,
  (state) => state.flickUserId,
);
export const getTransactionId = createSelector(
  getCoreState,
  (state) => state.transactionId,
);

export const getCCAuthSuccessful = createSelector(
  getCoreState,
  (state) =>
    state.windcaveAuthorisationRequestStatus === REQUEST_STATUS.Fulfilled,
);

export const getSignUpState = createSelector(
  getCoreState,
  (state) => state.signUpState,
);

export const getCurrentFormStep = createSelector(
  getCoreState,
  (state) => state.currentFormStep,
);

export const getPartialSignUpDialogState = createSelector(
  getCoreState,
  (state) => state.partialSignUpDialogOpen,
);

export const getPromoCode = createSelector(
  getCoreState,
  (state) => state.promoCode,
);

export const getLeadSource = createSelector(
  getCoreState,
  (state) => state.leadSource,
);

export const getSelectedProduct = createSelector(
  getCoreState,
  (state) => state.selectedProduct,
);

export const getEvChargerInclusion = createSelector(
  getCoreState,
  (state) => state.productMetadata.includeEVcharger,
);

export const getSelectedEvChargerColor = createSelector(
  getCoreState,
  (state) => state.productMetadata.evChargerColor,
);

export const getProductMetadata = createSelector(
  getCoreState,
  (state) => state.productMetadata,
);

export const getUsageType = createSelector(
  getCoreState,
  (state) => state.user.property.usageType,
);

export const getUser = createSelector(getCoreState, (state) => state.user);
export const getUserDetails = createSelector(getUser, (user) => user.details);
export const getUserPayment = createSelector(getUser, (user) => user.payment);
export const getUserProperty = createSelector(getUser, (user) => user.property);
export const getUserRewards = createSelector(getUser, (user) => user.rewards);

export const getUserVerifiedDetail = createSelector(
  getUserDetails,
  (details) => details.mobileNumber,
);

export const getSignupLitres = createSelector(getCoreState, (state) => {
  return state.campaigns[0]?.campaignLitres;
});

export const getCreationSource = createSelector(getCoreState, (state) => {
  return state.creationSource;
});
